import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Typography,
    Box,
    IconButton,
    Slide,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CloseIcon from '@mui/icons-material/Close';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import React from 'react';
import Button from '../components/common/Button';

// Transition
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

const StyledDialog = styled(Dialog)(({ theme }) => ({
    '& .MuiBackdrop-root': {
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        backdropFilter: 'blur(4px)',
    },
    '& .MuiDialog-paper': {
        borderRadius: 16,
        boxShadow: theme.shadows[16],
        maxWidth: 440,
        width: '100%',
        margin: theme.spacing(2),
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({ theme }) => ({
    padding: '20px 24px 12px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
    padding: '12px 24px 20px',
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
    padding: '16px 24px 24px',
    gap: 12,
    borderTop: `1px solid ${theme.palette.divider}`,
}));

const IconWrapper = styled(Box)(({ theme, color }) => ({
    width: 48,
    height: 48,
    borderRadius: 12,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginBottom: theme.spacing(2),
    backgroundColor: color === 'error'
        ? 'rgba(239, 68, 68, 0.1)'
        : 'rgba(105, 203, 249, 0.1)',
    color: color === 'error' ? '#EF4444' : theme.palette.primary.main,
}));

const HocModal = ({ open, onClose, children }) => {
    return (
        <Dialog
            fullScreen
            open={open}
            onClose={onClose}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
            TransitionComponent={Transition}
        >
            {children}
        </Dialog>
    );
};

const DeleteConfirmationModal = ({ open, onClose, onConfirm, title, message }) => {
    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <StyledDialogTitle>
                <Box />
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </StyledDialogTitle>
            <StyledDialogContent sx={{ textAlign: 'center' }}>
                <IconWrapper color="error" sx={{ mx: 'auto' }}>
                    <DeleteOutlineIcon />
                </IconWrapper>
                <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
                    {title || 'Confirm Deletion'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {message || 'Are you sure you want to delete this item? This action cannot be undone.'}
                </Typography>
            </StyledDialogContent>
            <StyledDialogActions sx={{ justifyContent: 'center' }}>
                <Button variant="secondary" onClick={onClose}>
                    Cancel
                </Button>
                <Button variant="danger" onClick={onConfirm}>
                    Delete
                </Button>
            </StyledDialogActions>
        </StyledDialog>
    );
};

const ViewConfirmationModal = ({ open, onClose, onConfirm, description }) => {
    return (
        <StyledDialog
            open={open}
            onClose={onClose}
            TransitionComponent={Transition}
        >
            <StyledDialogTitle>
                <Typography variant="h6" fontWeight={600}>
                    Examination Content
                </Typography>
                <IconButton
                    onClick={onClose}
                    size="small"
                    sx={{
                        color: 'text.secondary',
                        '&:hover': { color: 'text.primary' },
                    }}
                >
                    <CloseIcon fontSize="small" />
                </IconButton>
            </StyledDialogTitle>
            <StyledDialogContent>
                <Box
                    sx={{
                        bgcolor: 'background.subtle',
                        borderRadius: 2,
                        p: 2,
                    }}
                >
                    {description ? (
                        <Typography variant="body1" color="text.primary">
                            {description}
                        </Typography>
                    ) : (
                        <Box sx={{ textAlign: 'center', py: 2 }}>
                            <InfoOutlinedIcon sx={{ fontSize: 32, color: 'text.secondary', opacity: 0.5, mb: 1 }} />
                            <Typography variant="body2" color="text.secondary">
                                Description not available
                            </Typography>
                        </Box>
                    )}
                </Box>
            </StyledDialogContent>
            <StyledDialogActions>
                <Button variant="secondary" onClick={onClose} fullWidth>
                    Close
                </Button>
            </StyledDialogActions>
        </StyledDialog>
    );
};

export default ViewConfirmationModal;

export {
    HocModal,
    DeleteConfirmationModal,
    ViewConfirmationModal,
};
